import React, { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { AgentNodeResponseMapping } from "src/agentNode/AgentNodeResponseMapping";
import { AgentPromptEditor } from "src/agentNode/AgentPromptEditor";
import { AgentSelectionEditor } from "src/agentNode/AgentSelectionEditor";
import { AgentNode, AgentNodeData } from "src/constants/NodeDataTypes";
import { NodeEditorBaseProps } from "src/nodeEditor/NodeEditor";
import { useFormApplyNodeSuggestion } from "src/nodeEditor/applyNodeSuggestion";
import { useSubmitForm } from "src/utils/useSubmitForm";

const usePreviousValues = (nodeData?: AgentNodeData) => {
  return useMemo(() => nodeData, [nodeData]);
};

type Props = {
  selectedNode: AgentNode;
} & NodeEditorBaseProps<AgentNodeData>;

export const AgentNodeEditor: React.FC<Props> = ({
  selectedNode,
  immutable,
  isReactive,
  onUpdate,
}) => {
  const previousValues = usePreviousValues(selectedNode.data);

  const formMethods = useForm<AgentNodeData>({
    defaultValues: previousValues,
    ...(isReactive && { values: previousValues }),
  });

  useFormApplyNodeSuggestion<AgentNodeData>(selectedNode.id, (suggestion) => {
    if (suggestion.prompt) {
      formMethods.setValue("prompt", suggestion.prompt);
    }
  });

  useSubmitForm({
    onChange: (data: AgentNodeData) => {
      onUpdate({ newData: data });
    },
    disabled: isReactive,
    previousValues: previousValues,
    watch: formMethods.watch,
  });

  return (
    <FormProvider {...formMethods}>
      <div className="w-full space-y-3">
        <AgentSelectionEditor immutable={immutable} />
        <AgentPromptEditor immutable={immutable} isReactive={isReactive} />
        <AgentNodeResponseMapping immutable={immutable} />
      </div>
    </FormProvider>
  );
};
